import { useState } from "react";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";
import { MoreVertical, Truck, MessageSquare, CalendarClock, Camera, Navigation, MapPin, Pencil, Phone, CheckCircle2, XCircle, AlertTriangle, PackagePlus, DollarSign, CreditCard, Bell, Copy } from "lucide-react";
import RescheduleDialog from "./RescheduleDialog";
import OnMyWayDialog from "./OnMyWayDialog";
import MessageCustomerDialog from "./MessageCustomerDialog";
import PhotosUploadDialog from "./PhotosUploadDialog";
import EditBookingDialog from "./EditBookingDialog";
import LongGrassFeeDialog from "./LongGrassFeeDialog";
import AddExtraWorkDialog from "./AddExtraWorkDialog";
import UpdatePriceDialog from "./UpdatePriceDialog";
import ChangePaymentMethodDialog from "./ChangePaymentMethodDialog";
import ChangePaymentStatusDialog from "./ChangePaymentStatusDialog";
import AutoPayEnrollmentControl from "./AutoPayEnrollmentControl";
import { api } from "../lib/config";
import { toast } from "sonner";

const buildDirectionsUrl = (address) => {
  if (!address) return null;
  const encoded = encodeURIComponent(address);
  // Apple Maps on iOS / macOS will pick up `maps://`; everywhere else hits Google Maps in the browser
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod|Macintosh/.test(ua)) return `maps://?daddr=${encoded}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
};

const PAYMENT_STATUS_LABELS = {
  paid: { label: "Paid",             classes: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  paid_in_full: { label: "Paid",     classes: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  deposit_paid: { label: "Deposit Paid", classes: "bg-amber-100 text-amber-800 border-amber-200" },
  pending_payment_verification: { label: "Payment Pending", classes: "bg-gray-100 text-gray-700 border-gray-200" },
  // iter168 — Unpaid becomes "Overdue" (orange) when the visit is
  // already completed; otherwise "Payment Pending" (grey) pre-service.
  unpaid: { label: "Payment Pending", classes: "bg-gray-100 text-gray-700 border-gray-200" },
  // iter185 — Auto-charge attempt failed on Mark Complete. Job stays
  // Completed. Admin/customer notified; can pay later.
  payment_pending:   { label: "Payment Pending", classes: "bg-amber-100 text-amber-900 border-amber-300" },
  payment_failed:{ label: "AutoPay Failed", classes: "bg-rose-100 text-rose-800 border-rose-200" },
  autopay_failed:{ label: "AutoPay Failed", classes: "bg-rose-100 text-rose-800 border-rose-200" },
  // iter185 — Charge is running right now (post-completion), between
  // click and Stripe webhook. Usually a few seconds.
  payment_processing: { label: "Payment Processing", classes: "bg-blue-100 text-blue-800 border-blue-200" },
  // iter95: future recurring visit with a saved card — will auto-charge
  // on completion. Shows up on calendar / route map so admin sees the
  // full pipeline of upcoming visits.
  autopay_scheduled: { label: "AutoPay Scheduled", classes: "bg-violet-100 text-violet-800 border-violet-200" },
  // iter168 — Business-owner-approved label change from "Pay on Arrival"
  // to "Pay After Service" (the crew collects after finishing the job).
  pay_on_arrival: { label: "Pay After Service", classes: "bg-sky-100 text-sky-800 border-sky-200" },
  // iter168 — Derived overdue treatment (client-side promotion when the
  // visit is complete but payment still not received).
  overdue: { label: "Overdue", classes: "bg-orange-100 text-orange-900 border-orange-300" },
};

const _UNPAID_SET = new Set(["unpaid", "payment_pending", "payment_failed", "autopay_failed", "pending_payment_verification", "autopay_scheduled", "overdue"]);

export function PaymentBadge({ paymentStatus, bookingId, onMarkPaid, booking }) {
  // iter168 — Derive an "overdue" pill when the visit is already
  // completed but payment is still outstanding, so the crew can spot
  // aged-unpaid stops at a glance on the route map.
  let s = paymentStatus || "unpaid";
  const jobStatus = (booking?.status || "").toLowerCase();
  const unpaidLike = ["unpaid", "payment_pending", "payment_failed", "pending_payment_verification"].includes(s);
  if (unpaidLike && jobStatus === "completed") s = "overdue";
  const meta = PAYMENT_STATUS_LABELS[s] || PAYMENT_STATUS_LABELS.unpaid;
  const [busy, setBusy] = useState(false);
  const clickable = !!onMarkPaid && !!bookingId && _UNPAID_SET.has(s);

  const handleClick = async (e) => {
    if (!clickable || busy) return;
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm("Mark this booking as Paid?")) return;
    setBusy(true);
    try {
      await onMarkPaid(bookingId);
    } finally {
      setBusy(false);
    }
  };

  const base = `inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${meta.classes}`;
  if (clickable) {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        title="Mark as Paid"
        aria-label={`Mark booking ${bookingId} as paid`}
        data-testid={`payment-badge-${s}-clickable`}
        className={`${base} hover:brightness-95 hover:ring-2 hover:ring-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer transition disabled:opacity-60`}
      >
        {busy ? "…" : meta.label}
      </button>
    );
  }
  return (
    <span data-testid={`payment-badge-${s}`} className={base}>
      {meta.label}
    </span>
  );
}

const STATUS_META = {
  scheduled:   { label: "Scheduled",   classes: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  confirmed:   { label: "Scheduled",   classes: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  pending:     { label: "Pending",     classes: "bg-gray-100 text-gray-700 border-gray-200" },
  on_the_way:  { label: "On The Way",  classes: "bg-amber-100 text-amber-800 border-amber-200" },
  arrived:     { label: "Arrived",     classes: "bg-amber-100 text-amber-800 border-amber-200" },
  in_progress: { label: "In Progress", classes: "bg-blue-100 text-blue-800 border-blue-200" },
  completed:   { label: "Completed",   classes: "bg-emerald-200 text-emerald-900 border-emerald-300" },
  cancelled:   { label: "Cancelled",   classes: "bg-rose-100 text-rose-800 border-rose-200" },
  canceled:    { label: "Cancelled",   classes: "bg-rose-100 text-rose-800 border-rose-200" },
  rescheduled: { label: "Rescheduled", classes: "bg-amber-100 text-amber-800 border-amber-200" },
};

export function StatusBadge({ status }) {
  const meta = STATUS_META[status || "scheduled"] || STATUS_META.scheduled;
  return (
    <span data-testid={`status-badge-${status || "scheduled"}`} className={`inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${meta.classes}`}>
      {meta.label}
    </span>
  );
}

// iter101 — Small "Duplicate" pill shown on bookings that have been
// flagged / archived as duplicates. Renders nothing when the booking is
// not a duplicate.
export function DuplicateBadge({ booking }) {
  if (!booking) return null;
  const isDup = !!(booking.duplicate_of_booking_id || booking.archived_reason?.startsWith?.("duplicate_of_"));
  if (!isDup) return null;
  return (
    <span
      data-testid={`duplicate-badge-${booking.booking_id}`}
      className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-300"
      title={`Duplicate of ${booking.duplicate_of_booking_id || "another booking"}`}
    >
      <Copy className="w-2.5 h-2.5" /> Duplicate
    </span>
  );
}

export default function JobActionsMenu({ booking, onUpdated }) {
  const [resched, setResched] = useState(false);
  const [omw, setOmw] = useState(false);
  const [msg, setMsg] = useState(false);
  const [photos, setPhotos] = useState(false);
  const [edit, setEdit] = useState(false);
  const [longGrass, setLongGrass] = useState(false);
  const [extraWork, setExtraWork] = useState(false);
  const [updatePrice, setUpdatePrice] = useState(false);
  const [changePM, setChangePM] = useState(false);
  const [changePS, setChangePS] = useState(false);
  const [busy, setBusy] = useState(null);
  const [remindBusy, setRemindBusy] = useState(false);

  if (!booking) return null;
  const directionsUrl = buildDirectionsUrl(booking.address);
  const telUrl = booking.phone ? `tel:${String(booking.phone).replace(/[^0-9+]/g, "")}` : null;
  const isTerminal = ["completed", "cancelled", "canceled"].includes((booking.status || "").toLowerCase());
  const isCompleted = (booking.status || "").toLowerCase() === "completed";
  const isPaid = (booking.payment_status || "").toLowerCase() === "paid";
  const canRemindToPay = isCompleted && !isPaid;

  // Payment method label shown in the menu so admin sees current before changing.
  const PM_LABEL = {
    card_on_file: "Card on File", stripe: "Stripe Checkout", card: "Stripe Checkout",
    cash_on_arrival: "Cash on Arrival", cash: "Cash on Arrival",
    zelle: "Zelle", cashapp: "Cash App", venmo: "Venmo",
    applepay: "Apple Pay", googlepay: "Google Pay",
  };
  const currentPmKey = (booking.payment_method || "cash_on_arrival").toLowerCase();
  const currentPmLabel = booking.payment_method_label || PM_LABEL[currentPmKey] || "Cash on Arrival";
  // Minimal customer shape needed by ChangePaymentMethodDialog. The dialog
  // reads user_id + name + preferred_payment_method (falls back to
  // recurring_schedule.payment_method). We synthesize from the booking so
  // admin can change directly from the 3-dot menu without leaving the page.
  const customerShim = {
    user_id: booking.user_id,
    name: booking.name,
    preferred_payment_method: currentPmKey,
  };

  const markComplete = async () => {
    if (isTerminal) return;
    if (!window.confirm(`Mark ${booking.service_name} for ${booking.name} complete?`)) return;
    setBusy("complete");
    try {
      await api.post(`/admin/bookings/${booking.booking_id}/status`, { status: "completed", note: "Marked complete from job actions" });
      toast.success("Marked complete — customer notified");
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark complete");
    } finally { setBusy(null); }
  };

  // iter101/102 — Find + remove duplicate bookings. Calls the backend to
  // detect candidates (broadened in iter102: same customer + same address
  // + same service, any date). Walks the admin through the safety rules
  // (Completed/Paid always wins, both-paid override, etc.).
  const findAndMergeDuplicate = async () => {
    setBusy("dup");
    try {
      const { data } = await api.get(`/admin/bookings/${booking.booking_id}/duplicates`);
      const dups = data.duplicates || [];
      if (dups.length === 0) {
        toast.success("No other bookings match for this customer + address + service.");
        return;
      }
      const src = data.source;

      // If multiple candidates, ask the admin which one to compare against.
      let dup = dups[0];
      if (dups.length > 1) {
        const list = dups.map((d, i) =>
          `${i + 1}. ${d.booking_id} — ${d.preferred_date} · ${d.preferred_time} · ${d.payment_status} · ${d.status}`
        ).join("\n");
        const pick = window.prompt(
          `${dups.length} candidate bookings match. Which one is the duplicate? (Enter 1-${dups.length})\n\n${list}`,
          "1"
        );
        const idx = parseInt(pick, 10) - 1;
        if (isNaN(idx) || idx < 0 || idx >= dups.length) {
          toast("Cancelled — no selection made.");
          return;
        }
        dup = dups[idx];
      }

      // Rank each booking by "wins over the other" priority:
      //   completed+paid > paid > autopay_scheduled > completed > scheduled > unpaid
      const rank = (b) => {
        const st = (b.status || "").toLowerCase();
        const ps = (b.payment_status || "").toLowerCase();
        if (st === "completed" && ps === "paid") return 5;
        if (ps === "paid") return 4;
        if (ps === "autopay_scheduled") return 3;
        if (st === "completed") return 2;
        if (st === "scheduled") return 1;
        return 0;
      };
      const srcRank = rank(src);
      const dupRank = rank(dup);
      let keep = src, del = dup, override = false;
      if (dupRank > srcRank) { keep = dup; del = src; }
      // Both paid → warn + require override
      const srcPaid = ["paid", "autopay_scheduled"].includes((src.payment_status || "").toLowerCase());
      const dupPaid = ["paid", "autopay_scheduled"].includes((dup.payment_status || "").toLowerCase());
      if (srcPaid && dupPaid) {
        const proceed = window.confirm(
          "⚠️ BOTH bookings are PAID. Are you SURE you want to archive one?\n\n" +
          `Keeping: ${src.booking_id} ($${src.price?.toFixed(2)}, ${src.payment_status})\n` +
          `Archiving: ${dup.booking_id} ($${dup.price?.toFixed(2)}, ${dup.payment_status})`
        );
        if (!proceed) return;
        override = true;
      }

      const confirmMsg =
        `Remove this duplicate booking?\n\n` +
        `Customer: ${keep.name}\n` +
        `Service: ${keep.service_name}\n` +
        `Address: ${keep.address}\n\n` +
        `✅ KEEP: ${keep.booking_id}\n` +
        `   ${keep.preferred_date} · ${keep.preferred_time}\n` +
        `   ${keep.status} · ${keep.payment_status} · $${keep.price?.toFixed(2)}\n\n` +
        `🗑 ARCHIVE: ${del.booking_id}\n` +
        `   ${del.preferred_date} · ${del.preferred_time}\n` +
        `   ${del.status} · ${del.payment_status} · $${del.price?.toFixed(2)}\n\n` +
        `The customer, payments, invoices, and recurring schedule are NEVER deleted — only this booking will be archived. It can be restored from Archived Bookings.`;
      if (!window.confirm(confirmMsg)) return;

      const r = await api.post(`/admin/bookings/${booking.booking_id}/merge-duplicate`, {
        keep_id: keep.booking_id, delete_id: del.booking_id,
        confirm_override: override,
      });
      toast.success(r.data?.message || "Duplicate archived");
      onUpdated && onUpdated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : (detail || "Remove duplicate failed");
      toast.error(msg || "Remove duplicate failed");
    } finally { setBusy(null); }
  };

  const cancelJob = async () => {
    if (isTerminal) return;
    const reason = window.prompt("Cancellation reason (required, visible to customer):", "");
    if (!reason || reason.trim().length < 2) return;
    setBusy("cancel");
    try {
      await api.post(`/admin/bookings/${booking.booking_id}/cancel`, { reason: reason.trim() });
      toast.success("Job cancelled — customer notified");
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally { setBusy(null); }
  };

  const remindToPay = async () => {
    if (!canRemindToPay || remindBusy) return;
    setRemindBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.booking_id}/remind-to-pay`, {});
      const channels = data?.reminder?.delivery || {};
      const sentChannels = Object.entries(channels)
        .filter(([, v]) => v && v.status === "sent")
        .map(([k]) => k.toUpperCase())
        .join(" · ");
      toast.success(sentChannels
        ? `✅ Payment reminder sent — ${sentChannels}`
        : "✅ Payment reminder sent successfully");
      onUpdated && onUpdated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "cooldown") {
        toast.error(detail.message || "Please wait before sending another reminder.");
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "Could not send reminder");
        toast.error(msg);
      }
    } finally { setRemindBusy(false); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`job-actions-${booking.booking_id}`}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Job actions</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setResched(true)} disabled={isTerminal} data-testid={`action-reschedule-${booking.booking_id}`}>
            <CalendarClock className="w-4 h-4 mr-2" /> Reschedule Job
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setEdit(true)} disabled={isTerminal} data-testid={`action-edit-${booking.booking_id}`}>
            <Pencil className="w-4 h-4 mr-2" /> Edit Job
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setMsg(true)} data-testid={`action-message-${booking.booking_id}`}>
            <MessageSquare className="w-4 h-4 mr-2" /> Message Customer
          </DropdownMenuItem>
          {telUrl && (
            <DropdownMenuItem asChild data-testid={`action-call-${booking.booking_id}`}>
              <a href={telUrl} className="cursor-pointer">
                <Phone className="w-4 h-4 mr-2" /> Call Customer
              </a>
            </DropdownMenuItem>
          )}
          {directionsUrl && (
            <DropdownMenuItem asChild data-testid={`action-directions-${booking.booking_id}`}>
              <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                <Navigation className="w-4 h-4 mr-2" /> Get Directions
              </a>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={markComplete} disabled={isTerminal || busy === "complete"} data-testid={`action-complete-${booking.booking_id}`}>
            <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" /> Mark Complete
          </DropdownMenuItem>
          <DropdownMenuItem onClick={cancelJob} disabled={isTerminal || busy === "cancel"} data-testid={`action-cancel-${booking.booking_id}`} className="text-rose-700 focus:text-rose-700 focus:bg-rose-50">
            <XCircle className="w-4 h-4 mr-2" /> Cancel Job
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Field actions</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setOmw(true)} disabled={isTerminal} data-testid={`action-on-my-way-${booking.booking_id}`}>
            <Truck className="w-4 h-4 mr-2" /> On The Way · Send ETA
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setPhotos(true)} data-testid={`action-photos-${booking.booking_id}`}>
            <Camera className="w-4 h-4 mr-2" /> Before / After Photos
            {((booking.before_photos?.length || 0) + (booking.after_photos?.length || 0)) > 0 && (
              <span className="ml-auto text-[10px] font-extrabold text-emerald-700">
                {(booking.before_photos?.length || 0) + (booking.after_photos?.length || 0)}
              </span>
            )}
          </DropdownMenuItem>
          {(booking.service_key === "lawn_mowing" || true) && (
            <DropdownMenuItem onClick={() => setLongGrass(true)} disabled={isTerminal} data-testid={`action-long-grass-${booking.booking_id}`} className="text-amber-800 focus:text-amber-800 focus:bg-amber-50">
              <AlertTriangle className="w-4 h-4 mr-2" /> Long Grass Detected
              {booking.long_grass_fee?.amount > 0 && (
                <span className="ml-auto text-[10px] font-extrabold text-amber-700">
                  +${booking.long_grass_fee.amount.toFixed(0)}
                </span>
              )}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setExtraWork(true)} disabled={isTerminal} data-testid={`action-extra-work-${booking.booking_id}`} className="text-emerald-800 focus:text-emerald-800 focus:bg-emerald-50">
            <PackagePlus className="w-4 h-4 mr-2" /> Add Extra Work
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <div className="px-2 pt-1.5 pb-0.5 text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 flex items-center justify-between gap-2">
            <span>Payment</span>
            <span
              className="text-[10px] normal-case tracking-normal font-extrabold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 truncate max-w-[9rem]"
              data-testid={`action-current-payment-method-${booking.booking_id}`}
              title={currentPmLabel}
            >
              {currentPmLabel}
            </span>
          </div>
          <DropdownMenuSeparator />
          <div className="px-2 pt-1.5 pb-1">
            <AutoPayEnrollmentControl
              userId={booking.user_id}
              testIdPrefix={`booking-${booking.booking_id}-autopay`}
              onChange={() => { onUpdated?.(); }}
            />
          </div>
          <DropdownMenuItem
            onClick={() => setChangePM(true)}
            disabled={!booking.user_id}
            data-testid={`action-change-payment-method-${booking.booking_id}`}
          >
            <CreditCard className="w-4 h-4 mr-2 text-emerald-700" /> Change Payment Method
          </DropdownMenuItem>
          {canRemindToPay && (
            <DropdownMenuItem
              onClick={remindToPay}
              disabled={remindBusy}
              data-testid={`action-remind-to-pay-${booking.booking_id}`}
              className="text-emerald-800 focus:text-emerald-800 focus:bg-emerald-50"
            >
              <Bell className="w-4 h-4 mr-2 text-emerald-700" />
              {remindBusy ? "Sending reminder…" : "🔔 Remind to Pay"}
            </DropdownMenuItem>
          )}
          {/* iter225 — Manual admin-only payment-status control */}
          <DropdownMenuItem
            onClick={() => setChangePS(true)}
            data-testid={`action-change-payment-status-${booking.booking_id}`}
            className="text-blue-800 focus:text-blue-800 focus:bg-blue-50"
          >
            <DollarSign className="w-4 h-4 mr-2 text-blue-700" /> Change Payment Status
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setUpdatePrice(true)} data-testid={`action-update-price-${booking.booking_id}`}>
            <DollarSign className="w-4 h-4 mr-2" /> 💲 Update Price
          </DropdownMenuItem>
          {/* iter101 — Merge / Remove Duplicate */}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={findAndMergeDuplicate}
            disabled={busy === "dup"}
            data-testid={`action-find-duplicates-${booking.booking_id}`}
            className="text-amber-800 focus:text-amber-800 focus:bg-amber-50"
          >
            <Copy className="w-4 h-4 mr-2" /> {busy === "dup" ? "Checking…" : "Remove Duplicate"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <RescheduleDialog open={resched} onOpenChange={setResched} booking={booking} onRescheduled={onUpdated} />
      <EditBookingDialog open={edit} onOpenChange={setEdit} booking={booking} onUpdated={onUpdated} />
      <OnMyWayDialog open={omw} onOpenChange={setOmw} booking={booking} onSent={onUpdated} />
      <MessageCustomerDialog open={msg} onOpenChange={setMsg} booking={booking} />
      <PhotosUploadDialog open={photos} onOpenChange={setPhotos} booking={booking} onUpdated={onUpdated} />
      <LongGrassFeeDialog open={longGrass} onClose={() => setLongGrass(false)} booking={booking} onSaved={onUpdated} />
      <AddExtraWorkDialog open={extraWork} onOpenChange={setExtraWork} booking={booking} mode="admin" onSaved={onUpdated} />
      <UpdatePriceDialog open={updatePrice} onOpenChange={setUpdatePrice} booking={booking} onSaved={onUpdated} />
      <ChangePaymentMethodDialog
        open={changePM}
        onOpenChange={setChangePM}
        customer={customerShim}
        onChanged={() => onUpdated?.()}
      />
      <ChangePaymentStatusDialog
        open={changePS}
        onOpenChange={setChangePS}
        booking={booking}
        onChanged={() => onUpdated?.()}
      />
    </>
  );
}

export function GetDirectionsButton({ address, className = "" }) {
  const url = buildDirectionsUrl(address);
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      data-testid="get-directions-btn"
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10px] font-extrabold uppercase tracking-[0.14em] border border-white/20 ${className}`}>
      <MapPin className="w-3 h-3" /> Directions
    </a>
  );
}


function _timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

/**
 * Small chip that shows on an admin booking card when a payment reminder
 * has been sent for it — helps the admin see reminder history at a glance
 * without opening the booking (iter73).
 */
export function LastReminderChip({ booking }) {
  const at = booking?.last_payment_reminder_at;
  if (!at) return null;
  const tier = booking?.last_payment_reminder_tier || "manual";
  const isAuto = String(tier).startsWith("auto_");
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-200"
      data-testid={`last-reminder-chip-${booking.booking_id}`}
      title={`Last payment reminder (${tier}) at ${at}`}
    >
      <Bell className="w-3 h-3" />
      {isAuto ? "Auto reminded" : "Reminded"} · {_timeAgo(at)}
    </span>
  );
}
